import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useChatDock, MIN_DRAWER_WIDTH, type ChatViewMode } from 'components/ChatLauncher/ChatDockContext';
import { FULLSCREEN_CHAT_MIN_WIDTH } from 'components/AIChat/fullscreenChatLayout';
import { getChatPanelZIndex } from './chatPanelLayering';

interface DraggablePopperProps {
  anchorEl: HTMLButtonElement | null;
  open: boolean;
  placement?: 'bottom' | 'top' | 'left' | 'right';
  children: React.ReactNode;
  isMinimized?: boolean;
  viewMode: ChatViewMode;
  /** Element that is allowed to start a drag (e.g. header bar). */
  dragHandleRef?: React.RefObject<HTMLElement | null>;
}

export const DraggablePopper: React.FC<DraggablePopperProps> = ({
  anchorEl,
  open,
  placement = 'bottom',
  children,
  isMinimized,
  viewMode,
  dragHandleRef,
}) => {
  const theme = useTheme();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const popperRef = React.useRef<HTMLDivElement | null>(null);
  const drawerRef = React.useRef<HTMLDivElement | null>(null);
  const startPosRef = React.useRef<{ x: number; y: number } | null>(null);
  const startWidthRef = React.useRef<number | null>(null);

  const isFloating = viewMode === 'floating';
  const isDocked = viewMode === 'docked';
  const isExpanded = viewMode === 'expanded';

  // DraggablePopper is always used within ChatDockProvider, so this hook is safe
  const { drawerWidth, setDrawerWidth } = useChatDock();

  useEffect(() => {
    if (!open) setOffset({ x: 0, y: 0 });
  }, [open]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isFloating) return;

    const target = e.target as Element | null;

    if (dragHandleRef?.current && target) {
      if (!dragHandleRef.current.contains(target)) return;
    }

    setDragging(true);
    startPosRef.current = { x: e.clientX, y: e.clientY };
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !startPosRef.current) return;
    const deltaX = e.clientX - startPosRef.current.x;
    const deltaY = e.clientY - startPosRef.current.y;

    setOffset((prev) => {
      const next = { x: prev.x + deltaX, y: prev.y + deltaY };
      const rect = popperRef.current?.getBoundingClientRect();
      if (!rect) return next;

      const edgePadding = 8;
      const rightPadding = parseFloat(theme.spacing(3));
      const minLeft = edgePadding;
      const maxLeft = window.innerWidth - rect.width - rightPadding;
      const minTop = edgePadding;
      const maxTop = window.innerHeight - rect.height - edgePadding;

      const proposedLeft = rect.left + deltaX;
      const proposedTop = rect.top + deltaY;

      const clampedLeft = Math.min(maxLeft, Math.max(minLeft, proposedLeft));
      const clampedTop = Math.min(maxTop, Math.max(minTop, proposedTop));

      return {
        x: next.x + (clampedLeft - proposedLeft),
        y: next.y + (clampedTop - proposedTop),
      };
    });

    startPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    setDragging(false);
    setResizing(false);
    startPosRef.current = null;
    startWidthRef.current = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (!isDocked) return;
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    startPosRef.current = { x: e.clientX, y: e.clientY };
    startWidthRef.current = drawerWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };

  const handleResizeMouseMove = (e: MouseEvent) => {
    if (!resizing || !startPosRef.current || startWidthRef.current === null || !setDrawerWidth) return;

    const deltaX = startPosRef.current.x - e.clientX;
    const newWidth = startWidthRef.current + deltaX;
    const clampedWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(newWidth, window.innerWidth * 0.8));

    setDrawerWidth(clampedWidth);
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleResizeMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing]);

  if (isExpanded) {
    return createPortal(
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: getChatPanelZIndex(theme, viewMode),
          display: open && !isMinimized ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: open && !isMinimized ? 'auto' : 'none',
        }}
      >
        <Backdrop
          open={open && !isMinimized}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: 'min(85vw, 1100px)',
            minWidth: FULLSCREEN_CHAT_MIN_WIDTH,
            maxWidth: 'calc(100vw - 16px)',
            height: 'min(90vh, 900px)',
          }}
        >
          {children}
        </Box>
      </Box>,
      document.body,
    );
  }

  if (isDocked) {
    return createPortal(
      <Box
        ref={drawerRef}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: `${drawerWidth}px`,
          height: '100vh',
          zIndex: getChatPanelZIndex(theme, viewMode),
          display: open && !isMinimized ? 'flex' : 'none',
          transition: resizing ? 'none' : 'width 0.2s ease-in-out',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {children}
        </Box>
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: theme.spacing(0.5),
            cursor: 'ew-resize',
            zIndex: 30,
            backgroundColor: theme.palette.divider,
            transition: 'all 0.2s ease-in-out',
            pointerEvents: 'auto',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              width: '8px',
            },
            ...(resizing && {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.9,
              width: '8px',
            }),
          }}
        />
      </Box>,
      document.body,
    );
  }

  // Popper portals to document.body itself; wrapping it in another portal is redundant.
  return (
    <Popper
      open={open}
      sx={{
        zIndex: getChatPanelZIndex(theme, viewMode),
        display: isMinimized ? 'none' : 'block',
        pointerEvents: isMinimized ? 'none' : 'auto',
      }}
      ref={popperRef}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={[
        {
          name: 'offset',
          options: { offset: [offset.x, -offset.y] },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: { top: 8, right: parseFloat(theme.spacing(3)), bottom: 8, left: 8 },
          },
        },
      ]}
    >
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          cursor: dragHandleRef ? 'auto' : dragging ? 'grabbing' : 'grab',
          backgroundColor: 'transparent',
        }}
      >
        {children}
      </Box>
    </Popper>
  );
};
